import { useCallback, useEffect, useState } from 'react';

import {
  CreateGenerativeTaskInput,
  GenerativeTaskErrorType,
  GenerativeTaskStatusEnum,
} from '@/types/schema';

import { GenerativeTaskFragment } from '../../graphql/fragments/generated/GenerativeTaskFragment';
import { useCreateGenerativeTaskMutation } from '../../graphql/mutations/generated/createGenerativeTask';
import { useGenerativeTaskQuery } from '../../graphql/queries/generated/generativeTaskQuery';
import { useGenerativeTaskSubscription } from '../../graphql/subscriptions/generated/generativeTaskSubscription';
import useAIError from '../useAIError';

type UseGenerativeTaskProps = {
  onExecuteGenerativeTask?: {
    beforeExecute?: () => void;
    afterExecute?: () => void;
  };
  onError?: () => void;
  onTaskError?: (error?: GenerativeTaskErrorType | null) => void;
  onSuccess?: (gt: GenerativeTaskFragment) => void;
  onCreateSuccess?: (gtId: string) => void;
  pause?: boolean;
  autoCleanup?: boolean;
  suppressDefaultErrorHandling?: boolean;
};

const useGenerativeTask = (props: UseGenerativeTaskProps) => {
  const {
    onExecuteGenerativeTask,
    onError,
    onTaskError,
    onSuccess,
    onCreateSuccess,
    pause,
    autoCleanup,
    suppressDefaultErrorHandling = false,
  } = props;
  const [generativeTaskId, setGenerativeTaskId] = useState('');
  const [{ fetching }, createGenerativeTask] = useCreateGenerativeTaskMutation();
  const { handleAIError } = useAIError();
  const [generativeTask, setGenerativeTask] = useState<GenerativeTaskFragment | undefined | null>();

  const executeCreateGenerativeTask = useCallback(
    async (input: CreateGenerativeTaskInput) => {
      if (pause) {
        return;
      }
      onExecuteGenerativeTask?.beforeExecute?.();
      const data = await createGenerativeTask({
        input,
      });

      const taskError = data.data?.createGenerativeTask?.generativeTask?.error;
      if (taskError) {
        onTaskError?.(taskError);
      }
      const error = suppressDefaultErrorHandling ? Boolean(taskError) : handleAIError(taskError);
      const generativeTaskId = data.data?.createGenerativeTask?.generativeTask?.id;

      if (error || !generativeTaskId) {
        onError?.();
        return;
      }

      setGenerativeTaskId(generativeTaskId);
      onCreateSuccess?.(generativeTaskId);
      onExecuteGenerativeTask?.afterExecute?.();
      return data;
    },
    [
      createGenerativeTask,
      handleAIError,
      onCreateSuccess,
      onError,
      onExecuteGenerativeTask,
      onTaskError,
      pause,
      suppressDefaultErrorHandling,
    ],
  );

  /**
   * clears generative task id so that subscriptions and queries stop fetching
   * use after accepting or discarding suggestions
   */
  const cleanupGenerativeTask = useCallback(() => {
    setGenerativeTaskId('');
  }, []);

  const [{ data: generativeTaskSubscriptionData }] = useGenerativeTaskSubscription({
    variables: {
      data: {
        generativeTaskId,
      },
    },
    pause: !generativeTaskId,
  });

  const [{ data: generativeTaskQueryData }] = useGenerativeTaskQuery({
    variables: {
      filters: {
        generativeTaskId,
      },
    },
    pause: !generativeTaskId,
  });

  useEffect(() => {
    if (
      generativeTaskSubscriptionData &&
      generativeTaskSubscriptionData.generativeTask.generativeTaskId === generativeTaskId
    ) {
      setGenerativeTask(generativeTaskSubscriptionData.generativeTask.generativeTask);
    }

    if (generativeTaskQueryData && generativeTaskQueryData.generativeTask.generativeTask) {
      setGenerativeTask(generativeTaskQueryData.generativeTask.generativeTask);
    }
  }, [generativeTaskId, generativeTaskQueryData, generativeTaskSubscriptionData]);

  useEffect(() => {
    const taskError = generativeTaskQueryData?.generativeTask.generativeTask?.error;
    if (taskError) {
      onTaskError?.(taskError);
    }
    const error = suppressDefaultErrorHandling ? Boolean(taskError) : handleAIError(taskError);

    if (error) {
      onError?.();
      if (autoCleanup) {
        cleanupGenerativeTask();
      }
    }
  }, [
    autoCleanup,
    cleanupGenerativeTask,
    generativeTaskQueryData,
    handleAIError,
    onError,
    onTaskError,
    suppressDefaultErrorHandling,
  ]);

  useEffect(() => {
    if (generativeTask?.status === GenerativeTaskStatusEnum.Completed) {
      onSuccess?.(generativeTask);
      if (autoCleanup) {
        cleanupGenerativeTask();
      }
    }
  }, [generativeTask, autoCleanup, cleanupGenerativeTask, onSuccess]);

  const generativeTaskIdFromSubscription =
    generativeTaskSubscriptionData?.generativeTask.generativeTaskId;

  return {
    generativeTask,
    generativeTaskId,
    generativeTaskIdFromSubscription,
    fetching,

    executeCreateGenerativeTask,
    cleanupGenerativeTask,
  };
};

export default useGenerativeTask;
